import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /**
   * @type {boolean}
   */
  #listenersAttached = false;

  /**
   * @type {boolean}
   */
  #isClosing = false;

  /**
   * @type {number}
   */
  #lockedScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    this.#detachDialogListeners();

    if (this.refs.dialog?.open) {
      this.#restoreBodyScroll();
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;

    if ((minWidth && windowWidth < minWidth) || (maxWidth && windowWidth > maxWidth)) {
      this.closeDialog();
    }
  }, 80);

  /**
   * Locks page scroll while preserving current scroll position.
   */
  #lockBodyScroll() {
    this.#lockedScrollY = window.scrollY || document.documentElement.scrollTop || 0;

    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#lockedScrollY}px`;
  }

  /**
   * Restores page scroll position after dialog close.
   */
  #restoreBodyScroll() {
    const fallbackScrollY = Number.parseInt(document.body.style.top || '0', 10) * -1;
    const scrollY = this.#lockedScrollY || fallbackScrollY || 0;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    try {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    } catch (_error) {
      window.scrollTo(0, scrollY);
    }

    this.#lockedScrollY = 0;
  }

  /**
   * Attaches temporary listeners only while the dialog is open.
   */
  #attachDialogListeners = () => {
    const { dialog } = this.refs;

    if (this.#listenersAttached || !dialog.open) return;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
    dialog.addEventListener('cancel', this.#handleCancel);

    this.#listenersAttached = true;
  };

  /**
   * Removes temporary dialog listeners.
   */
  #detachDialogListeners() {
    const { dialog } = this.refs;

    if (!this.#listenersAttached) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.removeEventListener('cancel', this.#handleCancel);

    this.#listenersAttached = false;
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#isClosing) return;

    this.#lockBodyScroll();

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    window.setTimeout(this.#attachDialogListeners, 0);
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || this.#isClosing) return;

    this.#isClosing = true;
    this.#detachDialogListeners();

    dialog.classList.add('dialog-closing');

    try {
      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });
    } catch (_error) {
      // Close anyway if animation tracking fails.
    }

    this.#restoreBodyScroll();

    if (dialog.open) {
      dialog.close();
    }

    dialog.classList.remove('dialog-closing');

    this.#isClosing = false;
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * Public method used by declarative on:click handlers.
   *
   * @param {MouseEvent} event
   */
  closeDialogOnClickOutside = (event) => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    if (event.target === dialog || isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses Escape.
   *
   * Public method used by declarative on:keydown handlers.
   *
   * @param {KeyboardEvent} event
   */
  closeDialogOnEscapePress = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    this.closeDialogOnClickOutside(event);
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    this.closeDialogOnEscapePress(event);
  };

  /**
   * Prevents native cancel from bypassing cleanup.
   *
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    const target = event.target;

    if (!(target instanceof HTMLDialogElement) && !(target instanceof HTMLDetailsElement)) return;
    if (!target.hasAttribute('scroll-lock')) return;

    document.documentElement.toggleAttribute('scroll-lock', target.open);
  },
  { capture: true }
);